import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Drawer } from 'vaul';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

/**
 * Responsive modal component.
 * - Mobile (< 640px): Renders as a Vaul bottom-sheet drawer with drag-to-dismiss.
 * - Desktop (>= 640px): Renders as a centered dialog with backdrop click to close.
 */
export function Modal({ isOpen, onClose, title, children, size = 'md' }: ModalProps) {
  const isMobile = useIsMobile();

  // Lock body scroll for desktop modal
  useEffect(() => {
    if (!isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
    } else if (!isMobile) {
      document.body.style.overflow = '';
    }
    return () => {
      if (!isMobile) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, isMobile]);

  if (isMobile) {
    return (
      <Drawer.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 z-50 bg-black/60 dark:bg-black/80" />
          <Drawer.Content className="fixed inset-x-0 bottom-0 z-50 flex flex-col max-h-[85vh] rounded-t-2xl outline-none">
            {/* Gradient background matching SummaryCard */}
            <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-white to-stone-50 dark:from-stone-900 dark:via-stone-900 dark:to-stone-800 rounded-t-2xl" />

            {/* Subtle noise texture */}
            <div className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03] rounded-t-2xl" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            }} />

            <div className="relative border border-b-0 border-stone-200/60 dark:border-stone-700/50 rounded-t-2xl shadow-2xl shadow-stone-900/10 dark:shadow-black/30 flex flex-col overflow-hidden">
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-1">
                <Drawer.Handle className="w-10 h-1.5 rounded-full bg-stone-300 dark:bg-stone-600" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-2 pb-4">
                <Drawer.Title className="text-2xl font-serif text-stone-900 dark:text-stone-100">{title}</Drawer.Title>
                <button
                  onClick={onClose}
                  className="w-10 h-10 flex items-center justify-center rounded-full text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] overflow-y-auto flex-1">
                {children}
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    );
  }

  // Desktop: centered dialog with AnimatePresence for exit animations
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/80 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            className={`relative w-full ${sizeClasses[size]} max-h-[90vh] overflow-hidden`}
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            {/* Gradient background matching SummaryCard */}
            <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-white to-stone-50 dark:from-stone-900 dark:via-stone-900 dark:to-stone-800 rounded-2xl" />

            {/* Subtle noise texture */}
            <div className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03] rounded-2xl" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            }} />

            <div className="relative border border-stone-200/60 dark:border-stone-700/50 rounded-2xl shadow-2xl shadow-stone-900/10 dark:shadow-black/30">
              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-6 pb-4">
                <h2 className="text-2xl font-serif text-stone-900 dark:text-stone-100">{title}</h2>
                <button
                  onClick={onClose}
                  className="w-10 h-10 flex items-center justify-center rounded-full text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 pb-6 overflow-y-auto max-h-[calc(90vh-100px)]">
                {children}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
